import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import axiosConn from '../../axiosConn';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import { AuthContext } from '../../context/AuthContext';
import isVideoFile from '../../utils/isVideoFile';
import { createMediaUploader } from '../../utils/mediaUpload';
import './localOpsActivationProofSubmit.css';

const DEFAULT_ONLINE_TARGET = 20;
const DEFAULT_PLACEMENT_TARGET = 20;
const RESULT_OPTIONS = [
  ['', 'Select result'],
  ['successful_placement', 'Successful'],
  ['declined', 'Declined'],
  ['closed', 'Closed'],
  ['needs_follow_up', 'Follow-up'],
];
const PLACEMENT_TYPE_OPTIONS = [
  ['front_door_window', 'Door/window'],
  ['counter_register', 'Counter'],
  ['public_flyer_board', 'Flyer board'],
  ['other', 'Other'],
];
const LEAD_TAG_OPTIONS = [
  'interested',
  'asked_question',
  'declined',
  'strong_target',
  'needs_follow_up',
  'task_idea',
  'offering_idea',
];

const stripApi = (url) =>
  typeof url === 'string' ? url.replace(/\/api\/v1\/?$/, '') : '';

const getBackendOrigin = () =>
  stripApi(process.env.REACT_APP_AZURE_API_BASE_URL) ||
  stripApi(process.env.REACT_APP_API_BASE_URL_PROD) ||
  stripApi(process.env.REACT_APP_API_BASE_URL) ||
  'http://localhost:4000';

const safeText = (value, fallback = '') => {
  const clean = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean || fallback;
};

const mediaUrl = (media) => safeText(media?.url || media?.secure_url || '');

const emptyOnlineEntry = () => ({
  postUrl: '',
  screenshotMedia: {},
  note: '',
});

const emptyFlyerEntry = () => ({
  result: '',
  storefrontPhotoMedia: {},
  placementPhotoMedia: [],
  placementTypes: [],
  note: '',
});

const emptyLeadNote = () => ({
  text: '',
  tags: [],
});

const emptyBountyClaim = () => ({
  contactedSource: '',
  taskUrl: '',
  proofUrl: '',
  note: '',
});

const normalizeMedia = (media) => {
  const url = mediaUrl(media);
  if (!url) return {};
  return {
    url,
    type: media?.type === 'video' ? 'video' : 'image',
    thumbnailUrl: safeText(media?.thumbnailUrl),
  };
};

const normalizeOnlineEntry = (entry = {}) => ({
  _id: entry._id,
  postUrl: safeText(entry.postUrl),
  screenshotMedia: normalizeMedia(entry.screenshotMedia),
  note: safeText(entry.note),
});

const normalizeFlyerEntry = (entry = {}) => ({
  _id: entry._id,
  result: RESULT_OPTIONS.some(([value]) => value === entry.result)
    ? entry.result
    : '',
  storefrontPhotoMedia: normalizeMedia(entry.storefrontPhotoMedia),
  placementPhotoMedia: (Array.isArray(entry.placementPhotoMedia)
    ? entry.placementPhotoMedia
    : []
  )
    .map(normalizeMedia)
    .filter((media) => media.url),
  placementTypes: (Array.isArray(entry.placementTypes)
    ? entry.placementTypes
    : []
  ).filter((type) =>
    PLACEMENT_TYPE_OPTIONS.some(([allowed]) => allowed === type),
  ),
  note: safeText(entry.note),
});

const normalizeLeadNote = (entry = {}) => ({
  _id: entry._id,
  text: safeText(entry.text),
  tags: (Array.isArray(entry.tags) ? entry.tags : []).filter((tag) =>
    LEAD_TAG_OPTIONS.includes(tag),
  ),
});

const normalizeBountyClaim = (entry = {}) => ({
  _id: entry._id,
  contactedSource: safeText(entry.contactedSource),
  taskUrl: safeText(entry.taskUrl),
  proofUrl: safeText(entry.proofUrl),
  note: safeText(entry.note),
  status: safeText(entry.status, 'pending'),
});

export const normalizeActivationProofDraft = (proof = {}) => ({
  publicId: safeText(proof.publicId),
  campaignSlug: safeText(proof.campaignSlug),
  campaignTitle: safeText(proof.campaignTitle, 'Camden LocalOps activation proof'),
  regionSlug: safeText(proof.regionSlug, 'camden'),
  regionLabel: safeText(proof.regionLabel, 'Camden'),
  status: safeText(proof.status, 'draft'),
  shareEnabled: proof.shareEnabled === true,
  targetOnlineOutreachCount:
    Number(proof.targetOnlineOutreachCount) || DEFAULT_ONLINE_TARGET,
  targetSuccessfulPlacementCount:
    Number(proof.targetSuccessfulPlacementCount) || DEFAULT_PLACEMENT_TARGET,
  onlineOutreachEntries: (Array.isArray(proof.onlineOutreachEntries)
    ? proof.onlineOutreachEntries
    : []
  ).map(normalizeOnlineEntry),
  flyerPlacementEntries: (Array.isArray(proof.flyerPlacementEntries)
    ? proof.flyerPlacementEntries
    : []
  ).map(normalizeFlyerEntry),
  leadNotes: (Array.isArray(proof.leadNotes) ? proof.leadNotes : []).map(
    normalizeLeadNote,
  ),
  activationBountyClaims: (Array.isArray(proof.activationBountyClaims)
    ? proof.activationBountyClaims
    : []
  ).map(normalizeBountyClaim),
});

const withEditableDraftRows = (proof = {}) => {
  const next = normalizeActivationProofDraft(proof);
  return {
    ...next,
    onlineOutreachEntries: next.onlineOutreachEntries.length
      ? next.onlineOutreachEntries
      : [emptyOnlineEntry()],
    flyerPlacementEntries: next.flyerPlacementEntries.length
      ? next.flyerPlacementEntries
      : [emptyFlyerEntry()],
    leadNotes: next.leadNotes.length ? next.leadNotes : [emptyLeadNote()],
  };
};

export const getActivationProofProgress = (proof = {}) => {
  const onlineOutreachCount = (proof.onlineOutreachEntries || []).filter(
    (entry) => safeText(entry.postUrl) && mediaUrl(entry.screenshotMedia),
  ).length;
  const successfulPlacementCount = (proof.flyerPlacementEntries || []).filter(
    (entry) =>
      entry.result === 'successful_placement' &&
      mediaUrl(entry.storefrontPhotoMedia) &&
      (entry.placementPhotoMedia || []).some((media) => mediaUrl(media)),
  ).length;
  const declinedPlacementCount = (proof.flyerPlacementEntries || []).filter(
    (entry) => entry.result === 'declined',
  ).length;
  const closedPlacementCount = (proof.flyerPlacementEntries || []).filter(
    (entry) => entry.result === 'closed',
  ).length;
  const followUpPlacementCount = (proof.flyerPlacementEntries || []).filter(
    (entry) => entry.result === 'needs_follow_up',
  ).length;

  return {
    onlineOutreachCount,
    successfulPlacementCount,
    declinedPlacementCount,
    closedPlacementCount,
    followUpPlacementCount,
    targetOnlineOutreachCount:
      Number(proof.targetOnlineOutreachCount) || DEFAULT_ONLINE_TARGET,
    targetSuccessfulPlacementCount:
      Number(proof.targetSuccessfulPlacementCount) || DEFAULT_PLACEMENT_TARGET,
  };
};

const buildProofPayload = (proof) => ({
  campaignTitle: proof.campaignTitle,
  regionSlug: proof.regionSlug,
  regionLabel: proof.regionLabel,
  targetOnlineOutreachCount: proof.targetOnlineOutreachCount,
  targetSuccessfulPlacementCount: proof.targetSuccessfulPlacementCount,
  onlineOutreachEntries: (proof.onlineOutreachEntries || []).map(
    normalizeOnlineEntry,
  ).filter(
    (entry) =>
      safeText(entry.postUrl) ||
      mediaUrl(entry.screenshotMedia) ||
      safeText(entry.note),
  ),
  flyerPlacementEntries: (proof.flyerPlacementEntries || []).map(
    normalizeFlyerEntry,
  ).filter(
    (entry) =>
      safeText(entry.result) ||
      mediaUrl(entry.storefrontPhotoMedia) ||
      (entry.placementPhotoMedia || []).some((media) => mediaUrl(media)) ||
      (entry.placementTypes || []).length ||
      safeText(entry.note),
  ),
  leadNotes: (proof.leadNotes || []).map(normalizeLeadNote),
  activationBountyClaims: (proof.activationBountyClaims || []).map(
    normalizeBountyClaim,
  ),
});

const formatStatusLabel = (status) =>
  safeText(status, 'draft')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getOnlineEntryState = (entry = {}) => {
  const hasLink = !!safeText(entry.postUrl);
  const hasScreenshot = !!mediaUrl(entry.screenshotMedia);
  if (hasLink && hasScreenshot) {
    return { kind: 'complete', label: 'Complete' };
  }
  if (!hasLink && !hasScreenshot) {
    return { kind: 'missing', label: 'Missing link and screenshot' };
  }
  return {
    kind: 'missing',
    label: hasLink ? 'Missing screenshot' : 'Missing link',
  };
};

const getFlyerEntryState = (entry = {}) => {
  const result = safeText(entry.result);
  const hasStorefront = !!mediaUrl(entry.storefrontPhotoMedia);
  const hasPlacementPhoto = (entry.placementPhotoMedia || []).some((media) =>
    mediaUrl(media),
  );
  if (!result) {
    return { kind: 'missing', label: 'Select result' };
  }
  if (result !== 'successful_placement') {
    return { kind: 'logged', label: 'Logged, not counted' };
  }
  if (hasStorefront && hasPlacementPhoto) {
    return { kind: 'complete', label: 'Complete' };
  }
  if (!hasStorefront && !hasPlacementPhoto) {
    return { kind: 'missing', label: 'Missing storefront and flyer photos' };
  }
  return {
    kind: 'missing',
    label: hasStorefront ? 'Missing flyer photo' : 'Missing storefront photo',
  };
};

const getExistingProofLoadFailure = (err) => {
  const status = err?.response?.status;
  if (status === 403) {
    return {
      title: 'You do not have access to this activation proof.',
      message: 'Use the helper account that owns this proof or ask an admin.',
    };
  }
  if (status === 404) {
    return {
      title: 'Activation proof not found.',
      message: 'Check the proof link and try again.',
    };
  }
  return {
    title:
      err?.response?.data?.message || 'Activation proof could not be loaded.',
    message: 'Please refresh or try again in a moment.',
  };
};

const ProgressMeter = ({ label, value, target }) => {
  const ratio = target > 0 ? Math.min(value / target, 1) : 0;
  return (
    <div className="activation-proof-meter">
      <div className="activation-proof-meter__row">
        <span>{label}</span>
        <strong>
          {value}/{target}
        </strong>
      </div>
      <div className="activation-proof-meter__track">
        <span style={{ width: `${Math.round(ratio * 100)}%` }} />
      </div>
    </div>
  );
};

const MediaThumb = ({ media }) => {
  if (!mediaUrl(media)) return null;
  if (media?.type === 'video') {
    return (
      <video
        className="activation-proof-thumb"
        src={media.url}
        muted
        playsInline
        controls
      />
    );
  }
  return (
    <img
      className="activation-proof-thumb"
      src={media.thumbnailUrl || media.url}
      alt=""
    />
  );
};

export default function LocalOpsActivationProofSubmit() {
  const { proofId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { player } = useContext(AuthContext);
  const uploaderRef = useRef(null);
  if (!uploaderRef.current) uploaderRef.current = createMediaUploader({});
  const isNewProofRoute = proofId === 'new';
  const authConfig = useMemo(
    () =>
      player?.token
        ? { headers: { Authorization: `Bearer ${player.token}` } }
        : undefined,
    [player?.token],
  );

  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailure, setLoadFailure] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saveState, setSaveState] = useState('idle');

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(location.search || '');
    const regionSlug = params.get('regionSlug') || params.get('region');
    const regionLabel = params.get('regionLabel');
    const campaignTitle = params.get('campaignTitle');
    const taskId = params.get('taskId');
    const routeStateProof = location.state?.activationProofDraft;

    const loadProof = async () => {
      setLoading(true);
      setError('');
      setNotice('');
      setLoadFailure(null);
      setProof(null);
      try {
        if (
          !isNewProofRoute &&
          routeStateProof?.publicId &&
          routeStateProof.publicId === proofId
        ) {
          if (cancelled) return;
          setProof(withEditableDraftRows(routeStateProof));
          setSaveState('saved');
          setLoading(false);
          return;
        }
        const response =
          isNewProofRoute
            ? await axiosConn.post('local-ops/activation-proofs', {
                regionSlug,
                regionLabel,
                campaignTitle,
                taskId,
              }, authConfig)
            : await axiosConn.get(
                `local-ops/activation-proofs/${proofId}`,
                authConfig,
              );
        if (cancelled) return;
        const next = withEditableDraftRows(response?.data?.data || {});
        if (!next.publicId) {
          throw new Error('activation-proof-draft-missing-id');
        }
        setProof(next);
        setSaveState('saved');
        if (isNewProofRoute) {
          history.replace({
            pathname: `/localops/activation-proof/${next.publicId}/submit`,
            state: { activationProofDraft: next },
          });
        }
      } catch (err) {
        if (cancelled) return;
        setProof(null);
        setSaveState('idle');
        if (isNewProofRoute) {
          setLoadFailure({
            title: 'Activation proof setup is unavailable right now.',
            message: 'Please try again in a moment.',
            retryable: true,
            kind: 'new-draft',
          });
        } else {
          setLoadFailure({
            ...getExistingProofLoadFailure(err),
            retryable: true,
            kind: 'existing-proof',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProof();
    return () => {
      cancelled = true;
    };
  }, [
    history,
    authConfig,
    isNewProofRoute,
    loadAttempt,
    location.search,
    location.state,
    proofId,
  ]);

  const progress = useMemo(() => getActivationProofProgress(proof || {}), [proof]);
  const missingOnlineCount = Math.max(
    progress.targetOnlineOutreachCount - progress.onlineOutreachCount,
    0,
  );
  const missingPlacementCount = Math.max(
    progress.targetSuccessfulPlacementCount - progress.successfulPlacementCount,
    0,
  );
  const submitReady =
    !!proof?.publicId &&
    progress.onlineOutreachCount >= progress.targetOnlineOutreachCount &&
    progress.successfulPlacementCount >= progress.targetSuccessfulPlacementCount;
  const validationReasons = useMemo(() => {
    const reasons = [];
    if (!proof?.publicId) {
      reasons.push('Activation proof setup has not finished.');
    }
    if (missingOnlineCount > 0) {
      reasons.push(
        `Need ${missingOnlineCount} more online proof${
          missingOnlineCount === 1 ? '' : 's'
        }.`,
      );
    }
    if (missingPlacementCount > 0) {
      reasons.push(
        `Need ${missingPlacementCount} more successful flyer placement${
          missingPlacementCount === 1 ? '' : 's'
        }.`,
      );
    }
    if (missingPlacementCount > 0) {
      reasons.push(
        'Declined/closed/follow-up entries are saved but do not count toward the 20 successful placements.',
      );
    }
    return reasons;
  }, [missingOnlineCount, missingPlacementCount, proof?.publicId]);
  const locked = !proof?.publicId || ['submitted', 'approved'].includes(proof.status);
  const saveStatusLabel = saving
    ? 'Saving...'
    : saveState === 'dirty'
      ? 'Unsaved changes'
      : saveState === 'saved'
        ? 'Draft saved'
        : 'Draft ready';
  const publicShareUrl = proof?.publicId
    ? `${getBackendOrigin().replace(/\/+$/, '')}/share/localops/activation-proof/${proof.publicId}`
    : '';

  const retryLoad = useCallback(() => {
    setLoadAttempt((attempt) => attempt + 1);
  }, []);

  const markDirty = useCallback(() => {
    setNotice('');
    setSaveState('dirty');
  }, []);

  const patchProof = useCallback((patch) => {
    markDirty();
    setProof((prev) => (prev ? { ...prev, ...patch } : prev));
  }, [markDirty]);

  const updateOnlineEntry = useCallback((index, patch) => {
    markDirty();
    setProof((prev) => {
      if (!prev) return prev;
      const next = [...(prev.onlineOutreachEntries || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, onlineOutreachEntries: next };
    });
  }, [markDirty]);

  const updateFlyerEntry = useCallback((index, patch) => {
    markDirty();
    setProof((prev) => {
      if (!prev) return prev;
      const next = [...(prev.flyerPlacementEntries || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, flyerPlacementEntries: next };
    });
  }, [markDirty]);

  const updateLeadNote = useCallback((index, patch) => {
    markDirty();
    setProof((prev) => {
      if (!prev) return prev;
      const next = [...(prev.leadNotes || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, leadNotes: next };
    });
  }, [markDirty]);

  const updateBountyClaim = useCallback((index, patch) => {
    markDirty();
    setProof((prev) => {
      if (!prev) return prev;
      const next = [...(prev.activationBountyClaims || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, activationBountyClaims: next };
    });
  }, [markDirty]);

  const removeAt = useCallback((collection, index, fallback) => {
    markDirty();
    setProof((prev) => {
      if (!prev) return prev;
      const next = [...(prev[collection] || [])];
      next.splice(index, 1);
      return {
        ...prev,
        [collection]: next.length ? next : fallback ? [fallback()] : [],
      };
    });
  }, [markDirty]);

  const uploadFile = useCallback(async (file, onDone, key) => {
    if (!file || locked || !proof?.publicId) return;
    setUploadingKey(key);
    setError('');
    try {
      uploaderRef.current.validate(file);
      const { transformedUrl } = await uploaderRef.current.upload(file, {
        publicIdPrefix: 'Images',
      });
      onDone({
        url: transformedUrl,
        type: isVideoFile(file.name) ? 'video' : 'image',
      });
    } catch (err) {
      setError(err?.message || 'Media upload failed.');
    } finally {
      setUploadingKey('');
    }
  }, [locked, proof?.publicId]);

  const saveDraft = useCallback(async () => {
    if (!proof?.publicId || locked) return null;
    setSaving(true);
    setSaveState('saving');
    setError('');
    setNotice('');
    try {
      const response = await axiosConn.patch(
        `local-ops/activation-proofs/${proof.publicId}`,
        buildProofPayload(proof),
        authConfig,
      );
      const next = withEditableDraftRows(response?.data?.data || proof);
      setProof(next);
      setSaveState('saved');
      setNotice('Draft saved.');
      return next;
    } catch (err) {
      setError(err?.response?.data?.message || 'Draft could not be saved.');
      setSaveState('dirty');
      return null;
    } finally {
      setSaving(false);
    }
  }, [authConfig, locked, proof]);

  const submitProof = useCallback(async () => {
    if (!proof?.publicId || !submitReady || locked) return;
    setSubmitting(true);
    setError('');
    setNotice('');
    try {
      const saved = await saveDraft();
      if (!saved) return;
      const response = await axiosConn.post(
        `local-ops/activation-proofs/${saved.publicId}/submit`,
        undefined,
        authConfig,
      );
      setProof(withEditableDraftRows(response?.data?.data || saved));
      setSaveState('saved');
      setNotice('Proof submitted for admin approval.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Proof could not be submitted.');
    } finally {
      setSubmitting(false);
    }
  }, [authConfig, locked, proof?.publicId, saveDraft, submitReady]);

  if (loading) {
    return (
      <main className="activation-proof-submit">
        <section className="activation-proof-submit__state-card">
          <JGLocalOpsMark
            className="activation-proof-submit__mark"
            label="JG LocalOps"
          />
          <span className="activation-proof-kicker">
            Activation Proof Intake
          </span>
          <h1>
            {isNewProofRoute
              ? 'Setting up your activation proof...'
              : 'Loading activation proof...'}
          </h1>
          <p>
            {isNewProofRoute
              ? 'Preparing a real draft record before the form opens.'
              : 'Loading the saved proof record.'}
          </p>
        </section>
      </main>
    );
  }

  if (loadFailure || !proof?.publicId) {
    const failure = loadFailure || {
      title: 'Activation proof setup is unavailable right now.',
      message: 'Please try again in a moment.',
      retryable: true,
    };
    return (
      <main className="activation-proof-submit">
        <section className="activation-proof-submit__state-card unavailable">
          <JGLocalOpsMark
            className="activation-proof-submit__mark"
            label="JG LocalOps"
          />
          <span className="activation-proof-kicker">
            Activation Proof Intake
          </span>
          <h1>{failure.title}</h1>
          <p>{failure.message}</p>
          <div className="activation-proof-submit__state-actions">
            {failure.retryable ? (
              <button
                type="button"
                className="activation-proof-button primary"
                onClick={retryLoad}
              >
                <ReplayOutlinedIcon fontSize="small" />
                Retry
              </button>
            ) : null}
            <button
              type="button"
              className="activation-proof-button secondary"
              onClick={() => history.push('/playermap')}
            >
              <ArrowBackOutlinedIcon fontSize="small" />
              Back to LocalOps
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="activation-proof-submit">
      <header className="activation-proof-submit__header">
        <div>
          <JGLocalOpsMark
            className="activation-proof-submit__mark"
            label="JG LocalOps"
          />
          <span className="activation-proof-kicker">Activation Proof Intake</span>
          <h1>{proof.campaignTitle}</h1>
          <div className="activation-proof-submit__meta">
            <span>{proof.regionLabel}</span>
            <span className={`activation-proof-status ${proof.status}`}>
              {formatStatusLabel(proof.status)}
            </span>
            <span
              className={`activation-proof-save-state ${
                saveState === 'dirty' ? 'dirty' : 'saved'
              }`}
            >
              {saveStatusLabel}
            </span>
          </div>
          <p className="activation-proof-submit__slug">
            {proof.campaignSlug
              ? `Campaign source: ${proof.campaignSlug}`
              : 'Campaign source will appear after the draft is saved.'}
          </p>
        </div>
        <div className="activation-proof-submit__actions">
          <button
            type="button"
            className="activation-proof-button secondary"
            onClick={saveDraft}
            disabled={saving || locked || !proof.publicId}
          >
            <SaveOutlinedIcon fontSize="small" />
            Save draft
          </button>
          <button
            type="button"
            className="activation-proof-button primary"
            onClick={submitProof}
            disabled={submitting || saving || locked || !submitReady}
            title={
              submitReady
                ? 'Submit for review'
                : validationReasons.join(' ')
            }
          >
            <SendOutlinedIcon fontSize="small" />
            Submit for review
          </button>
        </div>
      </header>

      {error ? <div className="activation-proof-alert error">{error}</div> : null}
      {notice ? <div className="activation-proof-alert success">{notice}</div> : null}

      <section className="activation-proof-summary">
        <div className="activation-proof-summary__head">
          <div>
            <h2>Proof requirements</h2>
            <p>
              Save drafts anytime. Submit for review after both proof targets are
              complete.
            </p>
          </div>
          <span className={`activation-proof-ready-chip ${submitReady ? 'ready' : ''}`}>
            {submitReady ? 'Ready for review' : 'Requirements missing'}
          </span>
        </div>
        <div className="activation-proof-summary__meters">
          <ProgressMeter
            label="Online outreach"
            value={progress.onlineOutreachCount}
            target={progress.targetOnlineOutreachCount}
          />
          <ProgressMeter
            label="Successful flyer placements"
            value={progress.successfulPlacementCount}
            target={progress.targetSuccessfulPlacementCount}
          />
        </div>
        <div className="activation-proof-requirement-copy">
          <p>
            <strong>20 online proofs:</strong> Facebook post/page link +
            screenshot showing the original post and your LocalOps comment.
          </p>
          <p>
            <strong>20 successful flyer placements:</strong> storefront/sign
            photo + visible flyer placement photo.
          </p>
        </div>
        <div className="activation-proof-summary__meta">
          <span>Declined {progress.declinedPlacementCount}</span>
          <span>Closed {progress.closedPlacementCount}</span>
          <span>Follow-up {progress.followUpPlacementCount}</span>
        </div>
        {!submitReady ? (
          <div className="activation-proof-validation">
            {validationReasons.map((reason) => (
              <p key={reason}>{reason}</p>
            ))}
          </div>
        ) : null}
      </section>

      {publicShareUrl && proof.status === 'approved' ? (
        <a className="activation-proof-public-link" href={publicShareUrl}>
          <LinkOutlinedIcon fontSize="small" />
          Public proof
        </a>
      ) : null}

      <section className="activation-proof-section">
        <div className="activation-proof-section__head">
          <div>
            <h2>Campaign</h2>
            <p>{proof.campaignSlug || 'Draft campaign slug pending'}</p>
          </div>
        </div>
        <div className="activation-proof-grid two">
          <label>
            Campaign title
            <input
              value={proof.campaignTitle}
              disabled={locked}
              onChange={(event) =>
                patchProof({ campaignTitle: event.target.value })
              }
            />
          </label>
          <label>
            Region label
            <input
              value={proof.regionLabel}
              disabled={locked}
              onChange={(event) =>
                patchProof({ regionLabel: event.target.value })
              }
            />
          </label>
        </div>
      </section>

      <section className="activation-proof-section">
        <div className="activation-proof-section__head">
          <div>
            <h2>Online outreach</h2>
            <p>{progress.onlineOutreachCount} qualifying entries</p>
          </div>
          <button
            type="button"
            className="activation-proof-icon-button"
            onClick={() =>
              patchProof({
                onlineOutreachEntries: [
                  ...proof.onlineOutreachEntries,
                  emptyOnlineEntry(),
                ],
              })
            }
            disabled={locked}
            title="Add online entry"
          >
            <AddOutlinedIcon fontSize="small" />
          </button>
        </div>
        <p className="activation-proof-helper-copy">
          Screenshot must show the original post/page and your LocalOps comment
          together.
        </p>
        <div className="activation-proof-entry-list">
          {proof.onlineOutreachEntries.map((entry, index) => {
            const entryState = getOnlineEntryState(entry);
            return (
              <article className="activation-proof-entry" key={entry._id || index}>
                <div className="activation-proof-entry__number">{index + 1}</div>
                <div className="activation-proof-entry__status-cell">
                  <span
                    className={`activation-proof-entry-state ${entryState.kind}`}
                  >
                    {entryState.label}
                  </span>
                </div>
                <label>
                  Facebook post/page link
                  <input
                    value={entry.postUrl}
                    disabled={locked}
                    onChange={(event) =>
                      updateOnlineEntry(index, { postUrl: event.target.value })
                    }
                  />
                </label>
                <label>
                  Optional note
                  <input
                    value={entry.note}
                    disabled={locked}
                    onChange={(event) =>
                      updateOnlineEntry(index, { note: event.target.value })
                    }
                  />
                </label>
                <div className="activation-proof-media-row">
                  <MediaThumb media={entry.screenshotMedia} />
                  <label className="activation-proof-upload">
                    <CloudUploadOutlinedIcon fontSize="small" />
                    Screenshot
                    <input
                      type="file"
                      accept="image/*,video/*"
                      disabled={locked}
                      onChange={(event) =>
                        uploadFile(
                          event.target.files?.[0],
                          (media) =>
                            updateOnlineEntry(index, {
                              screenshotMedia: media,
                            }),
                          `online-${index}`,
                        )
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className="activation-proof-icon-button danger"
                    onClick={() =>
                      removeAt('onlineOutreachEntries', index, emptyOnlineEntry)
                    }
                    disabled={locked}
                    title="Remove online entry"
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </button>
                </div>
                {uploadingKey === `online-${index}` ? (
                  <span className="activation-proof-uploading">Uploading...</span>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>

      <section className="activation-proof-section">
        <div className="activation-proof-section__head">
          <div>
            <h2>Flyer placements</h2>
            <p>{progress.successfulPlacementCount} successful placements</p>
          </div>
          <button
            type="button"
            className="activation-proof-icon-button"
            onClick={() =>
              patchProof({
                flyerPlacementEntries: [
                  ...proof.flyerPlacementEntries,
                  emptyFlyerEntry(),
                ],
              })
            }
            disabled={locked}
            title="Add flyer entry"
          >
            <AddOutlinedIcon fontSize="small" />
          </button>
        </div>
        <p className="activation-proof-helper-copy">
          Only successful placements with visible flyer placement photos count
          toward the 20 target.
        </p>
        <div className="activation-proof-entry-list">
          {proof.flyerPlacementEntries.map((entry, index) => {
            const entryState = getFlyerEntryState(entry);
            return (
              <article className="activation-proof-entry" key={entry._id || index}>
                <div className="activation-proof-entry__number">{index + 1}</div>
                <div className="activation-proof-entry__status-cell">
                  <span
                    className={`activation-proof-entry-state ${entryState.kind}`}
                  >
                    {entryState.label}
                  </span>
                </div>
                <label>
                  Result
                  <select
                    value={entry.result}
                    disabled={locked}
                    onChange={(event) =>
                      updateFlyerEntry(index, { result: event.target.value })
                    }
                  >
                    {RESULT_OPTIONS.map(([value, label]) => (
                      <option key={value || 'empty'} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Optional note
                  <input
                    value={entry.note}
                    disabled={locked}
                    onChange={(event) =>
                      updateFlyerEntry(index, { note: event.target.value })
                    }
                  />
                </label>
                <div className="activation-proof-checks">
                  {PLACEMENT_TYPE_OPTIONS.map(([value, label]) => (
                    <label key={value}>
                      <input
                        type="checkbox"
                        checked={entry.placementTypes.includes(value)}
                        disabled={locked}
                        onChange={(event) => {
                          const set = new Set(entry.placementTypes);
                          if (event.target.checked) set.add(value);
                          else set.delete(value);
                          updateFlyerEntry(index, {
                            placementTypes: Array.from(set),
                          });
                        }}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <div className="activation-proof-media-row">
                  <MediaThumb media={entry.storefrontPhotoMedia} />
                  <label className="activation-proof-upload">
                    <CloudUploadOutlinedIcon fontSize="small" />
                    Storefront/sign photo
                    <input
                      type="file"
                      accept="image/*,video/*"
                      disabled={locked}
                      onChange={(event) =>
                        uploadFile(
                          event.target.files?.[0],
                          (media) =>
                            updateFlyerEntry(index, {
                              storefrontPhotoMedia: media,
                            }),
                          `store-${index}`,
                        )
                      }
                    />
                  </label>
                  <label className="activation-proof-upload">
                    <CloudUploadOutlinedIcon fontSize="small" />
                    Flyer placement photo
                    <input
                      type="file"
                      accept="image/*,video/*"
                      disabled={locked}
                      onChange={(event) =>
                        uploadFile(
                          event.target.files?.[0],
                          (media) =>
                            updateFlyerEntry(index, {
                              placementPhotoMedia: [
                                ...(entry.placementPhotoMedia || []),
                                media,
                              ],
                            }),
                          `place-${index}`,
                        )
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className="activation-proof-icon-button danger"
                    onClick={() =>
                      removeAt('flyerPlacementEntries', index, emptyFlyerEntry)
                    }
                    disabled={locked}
                    title="Remove flyer entry"
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </button>
                </div>
                <div className="activation-proof-mini-media">
                  {(entry.placementPhotoMedia || []).map((media, mediaIndex) => (
                    <span key={`${media.url}-${mediaIndex}`}>
                      <MediaThumb media={media} />
                    </span>
                  ))}
                </div>
                {uploadingKey === `store-${index}` ||
                uploadingKey === `place-${index}` ? (
                  <span className="activation-proof-uploading">Uploading...</span>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>

      <section className="activation-proof-section">
        <div className="activation-proof-section__head">
          <div>
            <h2>Lead notes</h2>
            <p>Debrief: what did people say?</p>
          </div>
          <button
            type="button"
            className="activation-proof-icon-button"
            onClick={() =>
              patchProof({ leadNotes: [...proof.leadNotes, emptyLeadNote()] })
            }
            disabled={locked}
            title="Add lead note"
          >
            <AddOutlinedIcon fontSize="small" />
          </button>
        </div>
        <p className="activation-proof-helper-copy">
          Who seemed interested? Who asked questions? Who declined? Which
          businesses should JG follow up with? Any task or offering ideas?
        </p>
        <div className="activation-proof-entry-list compact">
          {proof.leadNotes.map((note, index) => (
            <article className="activation-proof-entry" key={note._id || index}>
              <TaskAltOutlinedIcon fontSize="small" />
              <label>
                Note
                <textarea
                  value={note.text}
                  disabled={locked}
                  onChange={(event) =>
                    updateLeadNote(index, { text: event.target.value })
                  }
                />
              </label>
              <label>
                Tag
                <select
                  value={note.tags[0] || ''}
                  disabled={locked}
                  onChange={(event) =>
                    updateLeadNote(index, {
                      tags: event.target.value ? [event.target.value] : [],
                    })
                  }
                >
                  <option value="">None</option>
                  {LEAD_TAG_OPTIONS.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="activation-proof-icon-button danger"
                onClick={() => removeAt('leadNotes', index, emptyLeadNote)}
                disabled={locked}
                title="Remove lead note"
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="activation-proof-section">
        <div className="activation-proof-section__head">
          <div>
            <h2>Activation Bounty Claims</h2>
            <p>{proof.activationBountyClaims.length} optional pending claims</p>
          </div>
          <button
            type="button"
            className="activation-proof-icon-button"
            onClick={() =>
              patchProof({
                activationBountyClaims: [
                  ...proof.activationBountyClaims,
                  emptyBountyClaim(),
                ],
              })
            }
            disabled={locked}
            title="Add activation bounty claim"
          >
            <AddOutlinedIcon fontSize="small" />
          </button>
        </div>
        <p className="activation-proof-helper-copy">
          You will earn an extra $10 Camden Task Activation Bounty per qualified
          LocalOps task you help activate that gets funded, completed, and
          verified. This is optional during initial proof submission.
        </p>
        <div className="activation-proof-entry-list compact">
          {proof.activationBountyClaims.map((claim, index) => (
            <article className="activation-proof-entry" key={claim._id || index}>
              <label>
                Contacted source
                <input
                  value={claim.contactedSource}
                  disabled={locked}
                  onChange={(event) =>
                    updateBountyClaim(index, {
                      contactedSource: event.target.value,
                    })
                  }
                />
              </label>
              <label>
                LocalOps task link
                <input
                  value={claim.taskUrl}
                  disabled={locked}
                  onChange={(event) =>
                    updateBountyClaim(index, { taskUrl: event.target.value })
                  }
                />
              </label>
              <label>
                Completed proof/APR link
                <input
                  value={claim.proofUrl}
                  disabled={locked}
                  onChange={(event) =>
                    updateBountyClaim(index, { proofUrl: event.target.value })
                  }
                />
              </label>
              <label>
                Optional note
                <input
                  value={claim.note}
                  disabled={locked}
                  onChange={(event) =>
                    updateBountyClaim(index, { note: event.target.value })
                  }
                />
              </label>
              <button
                type="button"
                className="activation-proof-icon-button danger"
                onClick={() =>
                  removeAt('activationBountyClaims', index, null)
                }
                disabled={locked}
                title="Remove activation lead"
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </button>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
